import React, { useState, useEffect, FC } from "react";
import { PaginationModel } from "../../models/Pagination";
import List from "../list/list2";
import Paginator from "../paginator/paginator";
import SearchField from "../search-field/search-field";
import { DEF_PG_SIZE_DROP_DOWN } from '../../constants/maximum-page-size';

import "./searchable-list.css";

type Props = {
    items: any[];
    title: string;
    onSelect: (index: number, item: any, label: string) => void;
    isStriped?: boolean;
    isMultiSelect?: boolean;
    selectedItems?: number[];
    searchBoxClassName?: string;
    titleClassName?: string;
    listClassName?: string;
    stripedListClassName?: string;
    listItemClassName?: string;
    stripedListItemClassName?: string;
    selectedListItemClassName?: string;
    onSearch?: (searchTerm) => void;
    pagination: PaginationModel;
    setPagination: (newPagination: PaginationModel) => void;
    pageSizeSet?: number[];
    searchable?: boolean;
};

/**
 *
 * @param param0
 * @returns
 */
const PaginatedSearchableList: FC<Props> = ({
    items,
    title,
    onSelect,
    isStriped = true,
    isMultiSelect = false,
    selectedItems = new Array(0),
    searchBoxClassName = "mx-2 p-1",
    titleClassName = "mx-2 mt-2 p-1",
    listClassName = "pt-1 pr-1 pl-1",
    stripedListClassName = "p-1",
    listItemClassName,
    stripedListItemClassName,
    selectedListItemClassName,
    onSearch = undefined, //default to search inside the component.
    pagination, //paginate externally
    setPagination, //paginate externally
    pageSizeSet = [DEF_PG_SIZE_DROP_DOWN],
    searchable = true
}) => {
    const [listItems, setListItems] = useState(new Array(0));

    const buildListItem = (item, index) => {        
        return {
            value: item.value,
            isSelected: selectedItems.includes(index),
            label: item.label,
            site: item.site,
            optionalColumns: item.optionalColumns
        };
    };

    useEffect(() => {
        setListItems(items.map(buildListItem));
    }, [items, selectedItems]);

    useEffect(() => {
        if (!!items) {
            const newItems = items.map(buildListItem);
            setListItems(newItems);
        }
    }, []);

    const handleSelection = (index, item, label) => {
        onSelect(index, item, label);
    }

    const handlePageNumberUpdate = (newPageNumber) => {
        setPagination({ ...pagination, pageNumber: newPageNumber });
    }

    const handlePageSizeUpdate = (newPageSize) => {
        setPagination({ ...pagination, maxPageSize: newPageSize });
    }
    const handleSearchTermUpdate = (newSearchTerm) => {
        if (onSearch) {
            onSearch(newSearchTerm);
        }
        else {
            localOnSearchTerm(newSearchTerm);
        }
    }

    const localOnSearchTerm = (searchTerm: string) => {
        const newItems = items.filter(item => item.label.toLowerCase().includes(searchTerm.toLocaleLowerCase()))
        const newListItems = newItems.map(buildListItem);
        setListItems(newListItems);
        setPagination({
            ...pagination,
            totalPages: newListItems.length / pagination.maxPageSize,
            totalCount: newListItems.length
        });
    }

    return (
        <>
            {searchable
                ? <SearchField
                    setSearchTerm={handleSearchTermUpdate}
                    className={searchBoxClassName}>
                </SearchField>
                : null}
            {title ? (
                <div
                    className={`flex flex-row border text-gray-700 rounded-t ${titleClassName}`}>
                    <p className="p-1 text-gray-700 text-xs font-bold">
                        {title}
                    </p>
                </div>
            ) : null}
            {pagination && pagination.totalPages > 1
                ? <Paginator
                    itemCount={listItems.length}
                    onPageNumberUpdated={handlePageNumberUpdate}
                    onPageSizeUpdated={handlePageSizeUpdate}
                    paginationObj={pagination}
                    sizes={pageSizeSet}>
                    {({ start, end }) =>
                        <div
                            className={isStriped
                                ? `text-gray-700 ${stripedListClassName}`
                                : `text-black ${listClassName}`}>
                            <List
                                onSelect={(index, item, label) => handleSelection(start < listItems.length ? start + index : index, item, label)}
                                isStriped={isStriped}
                                items={start < listItems.length
                                    ? listItems.slice(start, end + 1)
                                    : listItems} // the list of items are managed outside of the component, only one page is ever fed in.
                                isMultiSelect={isMultiSelect}
                                listItemClassName={listItemClassName}
                                stripedListItemClassName={stripedListItemClassName}
                                selectedListItemClassName={selectedListItemClassName}>
                            </List>
                        </div>
                    }
                </Paginator>
                : <div className={isStriped
                    ? `text-gray-700 ${stripedListClassName}`
                    : `text-black ${listClassName}`}>
                    <List
                        onSelect={(index, item, label) => handleSelection(index, item, label)}
                        isStriped={isStriped}
                        items={listItems}
                        isMultiSelect={isMultiSelect}
                        listItemClassName={listItemClassName}
                        stripedListItemClassName={stripedListItemClassName}
                        selectedListItemClassName={selectedListItemClassName}>
                    </List>
                </div>}
        </>
    );
};

export default PaginatedSearchableList;
